
























import { mixins } from "vue-class-component";
import { Component, Prop, Watch } from "vue-property-decorator";
import { Route } from "vue-router";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { SubscriptionsSearchResult } from "@scrile/api-provider/dist/api/SubscriptionProvider";
import { User } from "@scrile/api-provider/dist/api/UserProvider";
import {
  ContentFeed,
  ContentFeedSearchResult,
  InputContentFeedSearch,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import WithAuthUser from "@/mixins/WithAuthUser";
import WithContentFeed from "@/mixins/WithContentFeed";
import providers from "@/lib/api";
import { getPurchasedContentFeeds } from "@/lib/contentFeedHelper";
import emitter from "@/lib/emitter";
import { getProfileUrl } from "@/lib/urlHelpers";
import { resolveUserIdByRoute } from "@/lib/userHelpers";
import { feedFields } from "@/helpers";
import { EVENTS } from "@/const";
import PageProfileView from "./components/PageProfileView";

@Component({
  name: "PageProfile",
  components: { PageProfileView },
})
export default class PageProfile extends mixins(WithContentFeed, WithAuthUser) {
  @Prop({ type: String, required: true })
  public readonly userId!: string;

  @Prop({ type: String })
  public readonly feedId!: string;

  public loading: boolean = true;

  private user: UserPublicData | null = null;

  private questionaryInfo: Record<string, any> = {};

  private contentFeedSearchResult: ContentFeedSearchResult | null = null;

  public subscriptionLevels: SubscriptionLevel[] = [];

  public subscriptionsSearchResult: SubscriptionsSearchResult | null = null;

  public paymentPackages: PaymentPackage[] = [];

  public scroll: boolean = false;

  public isFollowing: 0 | 1 | -1 = 0;

  public get canUpdateContentSet() {
    return checkAuthorities(this.authUserAuthorities, this.isAuthor ? ["createMyContentSet"] : ["createAnyContentSet"]);
  }

  public get isSubscribed() {
    return (this.subscriptionsSearchResult?.result.length ?? 0) !== 0;
  }

  public get isAuthor() {
    return !!this.authUser && this.authUser.id === this.userId;
  }

  public onFeedClick(feedId: string) {
    if (this.user) this.$router.push(getProfileUrl(this.user, { feedId }));
  }

  public async onFollowUpdate(status: boolean) {
    this.isFollowing = status ? 1 : 0;
  }

  public onContentFeedUpdate(contentFeed: ContentFeed) {
    if (!this.contentFeedSearchResult) return;
    this.$set(
      this.contentFeedSearchResult,
      "result",
      this.contentFeedSearchResult.result.map(item => (item.id === contentFeed.id ? contentFeed : item))
    );
  }

  public onFeedDelete(feedId: string) {
    if (!this.contentFeedSearchResult) return;
    this.$set(
      this.contentFeedSearchResult,
      "result",
      this.contentFeedSearchResult.result.filter(item => item.id !== feedId)
    );
  }

  public async onNext(cb?: Function) {
    try {
      if (this.contentFeedSearchResult && this.contentFeedSearchResult.hasNextPage) {
        await this.getContentFeed(this.userId, { page: this.contentFeedSearchResult.page + 1 }, false);
      }
    } finally {
      cb && cb();
    }
  }

  public async getContentFeed(
    userId: string,
    data?: Partial<InputContentFeedSearch>,
    replace: boolean = true,
    feedIdToFind?: string
  ) {
    try {
      this.scroll = false;
      let response = await this.contentFeedSearch(
        {
          userId: userId,
          sort: "PUBLISH_TIME,DESC",
          processingStatus: this.canUpdateContentSet ? undefined : "OK",
          published: true,
          includeScheduled: this.canUpdateContentSet,
          ...data,
        },
        feedIdToFind,
        feedFields()
      );
      if (replace) {
        this.contentFeedSearchResult = response;
      } else {
        this.contentFeedSearchResult = {
          ...response,
          result: [...(this.contentFeedSearchResult ? this.contentFeedSearchResult.result : []), ...response.result],
        };
      }
      return this.contentFeedSearchResult;
    } finally {
      this.scroll = this.contentFeedSearchResult?.hasNextPage ?? false;
    }
  }

  public async getFollowingData(authUserId: string, profileUserId: string) {
    this.isFollowing = -1;
    await providers.FollowingsProvider.searchFollowings({ ids: [profileUserId] }).then(r => {
      this.isFollowing = !!r.find(i => i.followerId === authUserId && i.userId === profileUserId) ? 1 : 0;
    });
  }

  public async getSubscriptions(authUserId: string, profileUserId: string) {
    providers.SubscriptionProvider.search({
      data: {
        providerId: profileUserId,
        consumerIds: [authUserId],
        expired: false,
      },
    }).then(r => (this.subscriptionsSearchResult = r));
  }

  public async getUser(userId: string = this.userId) {
    const response = await providers.UserPublicDataProvider.search({ data: { id: [userId] } });
    this.user = response.result[0];
  }

  @Watch("userId")
  public getProfileData(userId: string, oldUserId?: string) {
    this.loading = true;
    this.user = null as UserPublicData | null;
    this.questionaryInfo = {};
    this.contentFeedSearchResult = null;
    const promises: Promise<any>[] = [
      this.getUser(userId),
      providers.QuestionaryInfoProvider.find({ id: userId }).then(userQuestionaryInfo => {
        this.questionaryInfo = userQuestionaryInfo.fields;
      }),
      this.getContentFeed(userId, {}, true, this.feedId || undefined),
      providers.PaymentPackageProvider.findAll({ type: [PaymentPackageEnum.FILESET] }).then(
        r => (this.paymentPackages = r)
      ),
    ];
    if (this.authUser && !this.isAuthor) {
      promises.push(this.getFollowingData(this.authUser.id, userId));
      promises.push(this.getSubscriptions(this.authUser.id, userId));
    }
    Promise.all(promises).finally(() => {
      this.loading = false;
      if (this.user?.verified) {
        providers.SubscriptionLevelProvider.find({ userIds: [userId] }, [
          "id",
          "userId",
          "pricePackageId",
          { pricePackage: ["amount", { costs: ["cost", "currency"] }] },
          "contentCount",
        ]).then(res => (this.subscriptionLevels = res));
      }
      if (oldUserId && oldUserId !== userId) {
        window.scrollTo({ top: 0 });
      }
    });
  }

  @Watch("authUser")
  public updateDataForLogin(newAuthUser: User | null, oldAuthUser: User | null) {
    if (newAuthUser && !this.isAuthor) {
      this.getFollowingData(newAuthUser.id, this.userId);
    }
    if (!oldAuthUser && newAuthUser && newAuthUser.accountInfo.lastLoginTime && this.authAllowAccess) {
      const resultData = this.contentFeedSearchResult?.result ?? [];
      const userId = resultData.find(() => true)?.user.id;
      if (userId) {
        this.updateFeed({ userId });
      }
    }
  }

  public async updateFeed(payload: { userId: string; feedId?: string }) {
    if (this.authUser) {
      this.getSubscriptions(this.authUser.id, payload.userId);
    }
    if (!this.contentFeedSearchResult) {
      return this.getContentFeed(this.userId, {}, true);
    }

    this.$set(
      this.contentFeedSearchResult,
      "result",
      await getPurchasedContentFeeds(
        { authorId: payload.userId, purchasedFeedId: payload.feedId },
        this.contentFeedSearchResult,
        {
          includeScheduled: this.canUpdateContentSet,
        },
        feedFields()
      )
    );
  }

  public created() {
    this.getProfileData(this.userId);

    emitter.on<any>(EVENTS.CONTENT_FEED_UPDATED, this.updateFeed);
    emitter.on<any>(EVENTS.CONTENT_FEED_PURCHASED, this.updateFeed);
  }

  public destroyed() {
    emitter.off<any>(EVENTS.CONTENT_FEED_UPDATED, this.updateFeed);
    emitter.off<any>(EVENTS.CONTENT_FEED_PURCHASED, this.updateFeed);
  }

  public beforeRouteEnter(to: Route, from: Route, next: any) {
    resolveUserIdByRoute(to, from, next);
  }

  public beforeRouteUpdate(to: Route, from: Route, next: any) {
    resolveUserIdByRoute(to, from, next);
  }
}
